import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HeadphonesOutlinedIcon from "@mui/icons-material/HeadphonesOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import {
    AttemptedCard,
    UnattemptedCard,
    LockedCard,
    LockedPremiumCard,
} from "components/Dashboard/TestCard";
import { getMockTemplates } from "services/sectionService";
import { getIELTSPlan, getSectionTestState, findAttemptForTemplate } from "utils/ieltsAccess";

const SECTION_ICONS = {
    listening: HeadphonesOutlinedIcon,
    reading: AutoStoriesOutlinedIcon,
    writing: CreateOutlinedIcon,
    speaking: MicOutlinedIcon,
};

const SECTION_LABELS = {
    listening: "Listening",
    reading: "Reading",
    writing: "Writing",
    speaking: "Speaking",
};

function formatDuration(seconds) {
    if (!seconds) return "";
    const mins = Math.round(seconds / 60);
    return `~${mins} minutes`;
}

function mapTemplateToTest(template, index, plan, mockAttempts) {
    const sectionCount = template.sections?.length || 0;
    const duration = formatDuration(template.duration);
    const metaParts = [];
    if (sectionCount) metaParts.push(`${sectionCount} section${sectionCount > 1 ? 's' : ''}`);
    if (duration) metaParts.push(duration);

    const templateId = String(template._id || template.templateId || '');

    const matchedAttempt = findAttemptForTemplate(template, mockAttempts);

    const state = matchedAttempt ? "attempted" : getSectionTestState(templateId, plan);

    return {
        id: templateId,
        title: `Test ${index + 1}`,
        meta: metaParts.join(' • '),
        state,
        band: matchedAttempt ? (matchedAttempt.summary?.overall?.score ?? null) : undefined,
        attemptId: matchedAttempt?._id,
        sections: template.sections,
    };
}

function renderCard(test, sectionIcon, onAction) {
    const commonProps = {
        icon: sectionIcon,
        title: test.title,
        meta: test.meta,
    };

    switch (test.state) {
        case "attempted":
            return (
                <AttemptedCard
                    key={test.id}
                    {...commonProps}
                    band={test.band}
                    onViewResults={() => onAction("view_results", test)}
                />
            );
        case "unattempted":
        default:
            return (
                <UnattemptedCard
                    key={test.id}
                    {...commonProps}
                    onStartTest={() => onAction("start_test", test)}
                />
            );
        case "free_prompt":
            return (
                <UnattemptedCard
                    key={test.id}
                    {...commonProps}
                    onStartTest={() => onAction("free_prompt", test)}
                />
            );
        case "locked":
            return (
                <LockedCard
                    key={test.id}
                    {...commonProps}
                    onUnlock={() => onAction("unlock_section", test)}
                />
            );
    }
}

const CARD_WIDTH = 380;
const CARD_GAP = 24;
const SCROLL_BY = CARD_WIDTH + CARD_GAP;

export default function SectionTests({ section, hasAttempts = false, mockAttempts = [], onBack, onAction }) {
    const [rawTemplates, setRawTemplates] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const scrollRef = React.useRef(null);
    const dragRef = React.useRef({ isDragging: false, startX: 0, scrollLeft: 0 });

    const scroll = (dir) => {
        scrollRef.current?.scrollBy({ left: dir * SCROLL_BY, behavior: "smooth" });
    };

    const onMouseDown = (e) => {
        const el = scrollRef.current;
        dragRef.current = { isDragging: true, startX: e.pageX, scrollLeft: el.scrollLeft };
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
        el.style.scrollSnapType = "none";
    };

    const onMouseMove = (e) => {
        if (!dragRef.current.isDragging) return;
        const dx = e.pageX - dragRef.current.startX;
        scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx;
    };

    const onMouseUp = () => {
        const el = scrollRef.current;
        dragRef.current.isDragging = false;
        el.style.cursor = "grab";
        el.style.userSelect = "";
        el.style.scrollSnapType = "";
    };

    const label = SECTION_LABELS[section] || section;
    const SectionIcon = SECTION_ICONS[section] || HeadphonesOutlinedIcon;

    const premium = {
        title: "Talk to a Teacher",
        meta: "30 min session • Understand current level • Improvement roadmap",
        icon: SupportAgentOutlinedIcon,
        originalPrice: 999,
        discountedPrice: 499,
    };

    // Fetch templates when section changes — no plan logic here
    React.useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                const data = await getMockTemplates({
                    mockType: 'ielts',
                    name: label,
                    pageSize: 10,
                    pageNumber: 1,
                });

                const templates = Array.isArray(data) ? data : (data?.templates || []);

                console.log("[SectionTests] raw plan from session:", getIELTSPlan());
                console.log("[SectionTests] templates for", label, ":", templates.length);

                if (isMounted) {
                    setRawTemplates(templates);
                }
            } catch (error) {
                console.error("Failed to fetch section tests:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => { isMounted = false; };
    }, [section, label]);

    // Re-compute test states every time hasAttempts, mockAttempts, or the template list changes
    const tests = React.useMemo(() => {
        const plan = { ...getIELTSPlan(), hasAttempts };
        return rawTemplates.map((t, i) => mapTemplateToTest(t, i, plan, mockAttempts));
    }, [rawTemplates, hasAttempts, mockAttempts]);


    if (!section) return null;

    return (
        <Box
            sx={{
                backgroundColor: "#EEF2F7",
                width: "100%",
                px: "76px",
                py: "48px",
            }}
        >
            {/* Back button + Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "36px" }}>
                <IconButton
                    onClick={onBack}
                    sx={{
                        border: "1px solid #C8D3DF",
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        padding: "6px",
                        "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: "1.1rem", color: "#374151" }} />
                </IconButton>
                <Typography
                    sx={{
                        color: "#111827",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "1.4rem", md: "1.75rem" },
                    }}
                >
                    {label} Tests
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '48px' }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <Box sx={{ position: "relative" }}>
                    {/* Left arrow */}
                    <IconButton
                        onClick={() => scroll(-1)}
                        sx={{
                            position: "absolute", left: -44, top: "50%",
                            transform: "translateY(-50%)", zIndex: 1,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    {/* Scrollable row */}
                    <Box
                        ref={scrollRef}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseUp}
                        sx={{
                            display: "flex",
                            gap: `${CARD_GAP}px`,
                            overflowX: "auto",
                            scrollSnapType: "x mandatory",
                            pb: "8px",
                            cursor: "grab",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        {tests.map((test) => (
                            <Box
                                key={test.id}
                                sx={{ width: CARD_WIDTH, minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, flexShrink: 0, flexGrow: 0, scrollSnapAlign: "start", display: "flex", p: "4px" }}
                            >
                                {renderCard(test, SectionIcon, onAction)}
                            </Box>
                        ))}

                        {/* Premium card always last */}
                        <Box sx={{ width: CARD_WIDTH, minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, flexShrink: 0, flexGrow: 0, scrollSnapAlign: "start", display: "flex", p: "4px" }}>
                            <LockedPremiumCard
                                icon={premium.icon}
                                title={premium.title}
                                meta={premium.meta}
                                originalPrice={premium.originalPrice}
                                discountedPrice={premium.discountedPrice}
                                onUnlock={() => onAction("unlock_premium", { id: "premium", title: premium.title })}
                            />
                        </Box>
                    </Box>

                    {/* Right arrow */}
                    <IconButton
                        onClick={() => scroll(1)}
                        sx={{
                            position: "absolute", right: -44, top: "50%",
                            transform: "translateY(-50%)", zIndex: 1,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
